import { useEffect, useState, useMemo } from "react";
import { API, apiError, formatINR, formatDate } from "../lib/api";
import { Plus, Trash2, TrendingUp, TrendingDown, Wallet, X } from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { toast } from "sonner";

const EMPTY = { category: "Rent", description: "", amount: "", date: new Date().toISOString().slice(0, 10) };

export default function Accounts() {
  const [expenses, setExpenses] = useState([]);
  const [sales, setSales] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const today = new Date().toISOString().slice(0, 10);
  const from = today.slice(0, 8) + "01";

  const load = async () => {
    try {
      const [e, s] = await Promise.all([
        API.get("/expenses", { params: { date_from: from, date_to: today } }),
        API.get("/sales", { params: { date_from: from, date_to: today, limit: 5000 } }),
      ]);
      setExpenses(e.data); setSales(s.data.filter((x) => x.status !== "returned"));
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await API.post("/expenses", { ...form, amount: parseFloat(form.amount) });
      toast.success("Expense added"); setModal(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await API.delete(`/expenses/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const totals = useMemo(() => {
    const income = sales.reduce((s, x) => s + x.total, 0);
    const expense = expenses.reduce((s, x) => s + x.amount, 0);
    return { income, expense, net: income - expense };
  }, [sales, expenses]);

  const byCategory = useMemo(() => {
    const m = {};
    expenses.forEach((e) => { m[e.category] = (m[e.category] || 0) + e.amount; });
    return Object.entries(m).map(([name, value]) => ({ name, value }));
  }, [expenses]);

  const chart = useMemo(() => {
    const m = {};
    sales.forEach((s) => {
      const d = s.created_at.slice(0, 10);
      m[d] = m[d] || { date: d, income: 0, expense: 0 };
      m[d].income += s.total;
    });
    expenses.forEach((e) => {
      m[e.date] = m[e.date] || { date: e.date, income: 0, expense: 0 };
      m[e.date].expense += e.amount;
    });
    return Object.values(m).sort((a, b) => a.date.localeCompare(b.date));
  }, [sales, expenses]);

  const COLORS = ["#002FA7", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899"];

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-5" data-testid="accounts-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Finance · {formatDate(from)} → {formatDate(today)}</div>
          <h1 className="font-display text-3xl font-bold">Accounts</h1>
          <p className="text-sm text-slate-500 mt-1">Cash flow, income vs expenditure for the current month.</p>
        </div>
        <button onClick={() => setModal(true)} className="btn-primary" data-testid="add-expense-btn"><Plus className="w-4 h-4" /> Add Expense</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TotalCard label="Income" value={totals.income} icon={TrendingUp} color="green" />
        <TotalCard label="Expense" value={totals.expense} icon={TrendingDown} color="red" />
        <TotalCard label="Net Cash Flow" value={totals.net} icon={Wallet} color="blue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-display text-lg font-semibold mb-4">Income vs Expense</h3>
          <div className="h-64">
            <ResponsiveContainer>
              <LineChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }} />
                <Legend />
                <Line type="monotone" dataKey="income" stroke="#10B981" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="expense" stroke="#EF4444" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-display text-lg font-semibold mb-4">Expense by Category</h3>
          {byCategory.length === 0 ? <div className="text-sm text-slate-500 text-center py-12">No expenses this month</div> : (
            <div className="h-64">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={byCategory} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                    {byCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-slate-200 font-display font-semibold">Expenses This Month</div>
        {expenses.length === 0 ? <div className="p-10 text-sm text-slate-500 text-center">No expenses yet.</div> : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Date</th><th>Category</th><th>Description</th><th className="text-right">Amount</th><th className="text-right">Actions</th></tr></thead>
              <tbody>
                {expenses.map((e) => (
                  <tr key={e.id}>
                    <td>{formatDate(e.date)}</td>
                    <td><span className="tag tag-muted">{e.category}</span></td>
                    <td>{e.description}</td>
                    <td className="text-right font-medium">{formatINR(e.amount)}</td>
                    <td className="text-right">
                      <button onClick={() => del(e.id)} className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-display text-xl font-semibold">Add Expense</h3>
              <button type="button" onClick={() => setModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <L label="Category">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="inp">
                  {["Rent", "Electricity", "Salary", "Inventory Purchase", "Logistics", "Marketing", "Other"].map((c) => <option key={c}>{c}</option>)}
                </select>
              </L>
              <L label="Description"><input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="inp" /></L>
              <L label="Amount (₹)"><input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount-input" className="inp" /></L>
              <L label="Date"><input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="inp" /></L>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
              <button type="button" onClick={() => setModal(false)} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="save-expense-btn">Save</button>
            </div>
          </form>
        </div>
      )}
      <style>{`.inp{width:100%;border:1px solid #E2E8F0;border-radius:8px;padding:.5rem .75rem;font-size:.875rem;outline:none}.inp:focus{border-color:#002FA7;box-shadow:0 0 0 3px rgba(0,47,167,.15)}`}</style>
    </div>
  );
}

function TotalCard({ label, value, icon: Icon, color }) {
  const cols = { green: "from-[#047857] to-[#10B981]", red: "from-[#B91C1C] to-[#EF4444]", blue: "from-[#002FA7] to-[#1D4ED8]" }[color];
  return (
    <div className="stat-card">
      <div className="flex items-start justify-between">
        <div>
          <div className="overline text-[10px]">{label}</div>
          <div className="mt-2 font-display text-3xl font-bold tracking-tight">{formatINR(value)}</div>
        </div>
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${cols} text-white flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}
function L({ label, children }) { return <label className="block"><div className="text-xs font-semibold text-slate-600 mb-1.5">{label}</div>{children}</label>; }
